'use client'
import React, { useEffect, useRef } from 'react'
import { FaGoogle } from 'react-icons/fa'

// Official multi-color Google "G" mark (the same artwork Google provides for
// third-party "Sign in with Google" / brand buttons), for `logoStyle: 'color'`.
function GoogleMark({ logoStyle = 'color', size = 16 }) {
  if (logoStyle === 'mono') return <FaGoogle size={size} aria-hidden="true" />
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4818h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.6154z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.344 0-4.3282-1.5831-5.036-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z" fill="#34A853" />
      <path d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.996 8.996 0 000 9c0 1.4523.3477 2.8268.9573 4.0418L3.964 10.71z" fill="#FBBC05" />
      <path d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.4259 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z" fill="#EA4335" />
    </svg>
  )
}

/**
 * Renders a fully custom-styled "Add as Preferred Source" widget using Google's
 * Advanced JavaScript implementation (see
 * https://developers.google.com/search/docs/appearance/preferred-sources).
 *
 * We deliberately do NOT use the standard <div google-add-preferred-source-btn>
 * badge, because that renders Google's own fixed design. Instead we load the
 * library in "manual" mode and bind our own styled trigger element to
 * preferredSource.addPreferredSource(), so the widget can match the site's
 * design system configured in the Blog Page editor.
 *
 * `layout` is 'button' (a single inline button — 'solid' | 'outline' | 'ghost' |
 * 'text' via `variant`) or 'card' (a standalone card with the Google mark,
 * a headline, and a full-width CTA button below it).
 *
 * `label` and `headline` may be per-language objects ({ en, th, ... }) or a
 * legacy plain string. Pass `language` (the current page's language code) to
 * pick the right translation.
 *
 * A <noscript> deeplink fallback is included using the configured site URL,
 * per Google's documented deeplink pattern, for users without JS.
 */
export default function GooglePreferredSourceButton({ config = {}, language = 'en' }) {
  const btnRef = useRef(null)

  useEffect(() => {
    if (!config?.show) return

    const SCRIPT_ID = 'google-preferred-source-lib'

    const bind = () => {
      window.PREFERRED_SOURCE = window.PREFERRED_SOURCE || []
      window.PREFERRED_SOURCE.push((preferredSource) => {
        preferredSource.init({ theme: config.theme ?? 'light' })
        if (btnRef.current) {
          btnRef.current.onclick = () => preferredSource.addPreferredSource()
        }
      })
    }

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script')
      script.id = SCRIPT_ID
      script.async = true
      script.src = 'https://news.google.com/swg/js/v1/publisher.js'
      // "manual" prevents Google from auto-rendering its own badge — we render our own.
      script.setAttribute('preferred-sources-control', 'manual')
      document.head.appendChild(script)
    }

    bind()
  }, [config?.show, config?.theme])

  if (!config?.show) return null

  const pick = (val, fb) => ((typeof val === 'object' ? (val?.[language] || val?.en) : val) || fb)
  const label = pick(config.label, 'Add as Preferred Source')
  const headline = pick(config.headline, '')
  const deeplink = `https://www.google.com/preferences/source?q=${encodeURIComponent(config.url || '')}`
  const logoStyle = config.logoStyle ?? 'color'
  const layout = config.layout ?? 'button'

  // ── Card layout ──────────────────────────────────────────────────────────
  if (layout === 'card') {
    return (
      <div
        style={{
          display: 'flex', flexDirection: 'column', gap: 14,
          background: config.cardBg || '#1c1c1c',
          border: `${config.cardBorderWidth ?? 1}px solid ${config.cardBorderColor || 'rgba(255,255,255,0.5)'}`,
          borderRadius: config.cardRadius ?? 16,
          padding: config.cardPadding ?? 20,
          maxWidth: 380,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <GoogleMark logoStyle={logoStyle} size={30} />
          {headline && (
            <p style={{
              margin: 0,
              flex: 1,
              minWidth: 0,
              color: config.headlineColor || '#ffffff',
              fontSize: `${config.headlineSize ?? 1.1}rem`,
              fontWeight: config.headlineWeight ?? '700',
              lineHeight: 1.3,
            }}>
              {headline}
            </p>
          )}
        </div>
        <button
          ref={btnRef}
          type="button"
          aria-label={label}
          className="w-full transition-opacity hover:opacity-90"
          style={{
            background: config.ctaBg || '#f1f1f1',
            color: config.ctaTextColor || '#111827',
            borderRadius: `${config.ctaRadius ?? 8}px`,
            border: 'none',
            padding: '10px 16px',
            fontWeight: 700,
            fontSize: '0.9rem',
            cursor: 'pointer',
          }}
        >
          {label}
        </button>
        {/* No-JS fallback deeplink, per Google's documented deeplink implementation */}
        <noscript>
          <a href={deeplink}>{label}</a>
        </noscript>
      </div>
    )
  }

  // ── Button layout (default) ─────────────────────────────────────────────
  const variant = config.variant ?? 'solid'
  const accent  = config.bg || 'var(--color-main)'
  const onFill  = config.textColor || '#ffffff'

  const VARIANT_STYLE = {
    solid:   { background: accent, color: onFill, border: 'none' },
    outline: { background: 'transparent', color: accent, border: `1.5px solid ${accent}` },
    ghost:   { background: 'transparent', color: accent, border: 'none' },
    text:    { background: 'transparent', color: accent, border: 'none', textDecoration: 'underline', textUnderlineOffset: '3px', padding: '4px 2px' },
  }[variant] ?? { background: accent, color: onFill, border: 'none' }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={label}
        className="inline-flex items-center gap-1.5 whitespace-nowrap transition-opacity hover:opacity-80"
        style={{
          ...VARIANT_STYLE,
          borderRadius: `${config.borderRadius ?? 6}px`,
          fontSize: '0.8rem',
          fontWeight: 600,
          padding: variant === 'text' ? '4px 2px' : '6px 12px',
          cursor: 'pointer',
        }}
      >
        <GoogleMark logoStyle={logoStyle} size={13} />
        {label}
      </button>
      {/* No-JS fallback deeplink, per Google's documented deeplink implementation */}
      <noscript>
        <a href={deeplink}>{label}</a>
      </noscript>
    </>
  )
}